import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Users,
  User,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  X
} from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { listCustomers, createCustomer, updateCustomer, deleteCustomer as dbDeleteCustomer, customerIdExists } from '../utils/db/customers';
import { getProfile } from '../utils/db/profiles';

const CustomerManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [profileGst, setProfileGst] = useState('');

  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();

  useEffect(() => {
    if (user) {
      loadCustomers();
      loadProfile();
    }
  }, [user]);

  const loadProfile = async () => {
    try {
      const profile = await getProfile(user.id);
      if (profile && profile.gst_no) {
        setProfileGst(profile.gst_no);
      }
    } catch (e) {
      console.error("Failed to load profile", e);
    }
  };

  const loadCustomers = async () => {
    try {
      const rows = await listCustomers(user.id);
      setCustomers(rows);
    } catch (e) {
      toast.error('Failed to load customers');
    }
  };

  const openModal = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setValue('customerId', customer.customer_id);
      setValue('customerName', customer.customer_name);
      setValue('address', customer.address || '');
      setValue('email', customer.email || '');
      setValue('mobile', customer.mobile || '');
      setValue('gstNo', customer.gst_no || '');
      setValue('fuelCharge', customer.fuel_charge || 0);
      setValue('royaltyCharge', customer.royalty_charge || 0);
      setValue('gstPercent', customer.gst_percent || 0);
    } else {
      setEditingCustomer(null);
      reset();
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingCustomer(null);
    reset();
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      // Auto-determine state based on GST numbers on submit
      let finalState = 'same'; // Default
      if (data.gstNo && data.gstNo.length >= 2 && profileGst && profileGst.length >= 2) {
        const customerStateCode = data.gstNo.substring(0, 2);
        const profileStateCode = profileGst.substring(0, 2);
        finalState = customerStateCode === profileStateCode ? 'same' : 'other';
      } else if (editingCustomer && editingCustomer.customer_state) {
        finalState = editingCustomer.customer_state;
      }
      data.customerState = finalState;

      if (editingCustomer) {
        await updateCustomer(user.id, editingCustomer.id, data);
        toast.success('Customer updated successfully!');
      } else {
        // Prevent duplicate customer_id for this user
        const exists = await customerIdExists(user.id, data.customerId);
        if (exists) {
          toast.error('Customer ID already exists. Please use a different ID.');
          setIsLoading(false);
          return;
        }
        await createCustomer(user.id, data);
        toast.success('Customer added successfully!');
      }

      await loadCustomers();
      closeModal();
    } catch (error) {
      toast.error('Failed to save customer');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteCustomer = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await dbDeleteCustomer(user.id, customerId);
      await loadCustomers();
      toast.success('Customer deleted successfully!');
    } catch (e) {
      toast.error('Failed to delete customer');
    }
  };

  const filteredCustomers = customers.filter(customer =>
    customer.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.customer_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customer Management</h1>
          <p className="text-gray-600">Add/Edit/Delete customers (ID, name, GST, address, etc.)</p>
        </div>

      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search customers by name, ID, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Customers List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Customers ({filteredCustomers.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mobile
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  GST No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCustomers.length > 0 ? (
                filteredCustomers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {customer.customer_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {customer.customer_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {customer.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {customer.mobile}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {customer.gst_no}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate('/rates', { state: { editCustomer: customer } })}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteCustomer(customer.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    {searchTerm ? 'No customers found matching your search.' : 'No customers added yet.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Customer Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-3xl shadow-2xl rounded-xl bg-white">
            <div className="mt-1">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer ID *
                  </label>
                  <input
                    type="text"
                    {...register('customerId', {
                      required: 'Customer ID is required',
                      maxLength: {
                        value: 30,
                        message: 'Customer ID must be 30 characters or less'
                      }
                    })}
                    maxLength={30}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter customer ID"
                  />
                  {errors.customerId && (
                    <p className="mt-1 text-sm text-red-600">{errors.customerId.message}</p>
                  )}
                </div>

                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Name *
                  </label>
                  <input
                    type="text"
                    {...register('customerName', {
                      required: 'Customer name is required',
                      maxLength: {
                        value: 100,
                        message: 'Customer name must be 100 characters or less'
                      }
                    })}
                    maxLength={100}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter customer name"
                  />
                  {errors.customerName && (
                    <p className="mt-1 text-sm text-red-600">{errors.customerName.message}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address *
                  </label>
                  <textarea
                    {...register('address', {
                      required: 'Address is required',
                      maxLength: {
                        value: 100,
                        message: 'Address must be 100 characters or less'
                      }
                    })}
                    rows={3}
                    maxLength={100}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter complete address (max 100 characters)"
                  />
                  {errors.address && (
                    <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>
                  )}
                </div>

                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      },
                      maxLength: {
                        value: 100,
                        message: 'Email must be 100 characters or less'
                      }
                    })}
                    maxLength={100}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter email"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>

                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile *
                  </label>
                  <input
                    type="tel"
                    {...register('mobile', {
                      required: 'Mobile number is required',
                      pattern: {
                        value: /^[0-9]{10}$/,
                        message: 'Please enter a valid 10-digit mobile number'
                      },
                      maxLength: {
                        value: 10,
                        message: 'Mobile number must be 10 digits'
                      }
                    })}
                    maxLength={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter mobile"
                  />
                  {errors.mobile && (
                    <p className="mt-1 text-sm text-red-600">{errors.mobile.message}</p>
                  )}
                </div>

                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GST Number
                  </label>
                  <input
                    type="text"
                    {...register('gstNo', {
                      maxLength: {
                        value: 15,
                        message: 'GST number must be 15 characters or less'
                      },
                      pattern: {
                        value: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
                        message: 'Please enter a valid GST number (e.g., 27AABCM1234Z1Z5)'
                      }
                    })}
                    maxLength={15}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter GST number (optional)"
                  />
                </div>

                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fuel Charge (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('fuelCharge', {
                      min: { value: 0, message: 'Fuel % cannot be negative' },
                      max: { value: 100, message: 'Fuel % cannot exceed 100' }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter fuel charge percentage"
                  />
                  {errors.fuelCharge && (
                    <p className="mt-1 text-sm text-red-600">{errors.fuelCharge.message}</p>
                  )}
                </div>

                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SURCHARGE (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('royaltyCharge', {
                      min: { value: 0, message: 'Surcharge % cannot be negative' },
                      max: { value: 100, message: 'Surcharge % cannot exceed 100' }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                    placeholder="Enter surcharge percentage"
                  />
                  {errors.royaltyCharge && (
                    <p className="mt-1 text-sm text-red-600">{errors.royaltyCharge.message}</p>
                  )}
                </div>

                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GST Percentage (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('gstPercent', {
                      min: { value: 0, message: 'GST % cannot be negative' },
                      max: { value: 100, message: 'GST % cannot exceed 100' }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter GST percentage"
                  />
                  {errors.gstPercent && (
                    <p className="mt-1 text-sm text-red-600">{errors.gstPercent.message}</p>
                  )}
                </div>



                <div className="md:col-span-2 flex justify-end space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                  >
                    {isLoading ? 'Saving...' : (editingCustomer ? 'Update' : 'Add')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerManagement; 